<div class="w-full max-w-7xl items-center py-8">
  <section class="mb-8 flex lg:mb-0" aria-label="Register for My Account">
    <div
      class="w-full max-w-xl rounded-xl bg-white p-6 text-black shadow-lg md:p-10"
    >
      <!-- Comcast Business Logo -->
      <div class="mb-8 flex md:justify-center">
        <a
          routerLink="/"
          class="inline-block"
          aria-label="Comcast Business — go to homepage"
        >
          <img
            src="assets/images/logos/CB_Logo_Color_RGB.svg"
            alt=""
            class="h-10 w-auto md:h-14"
          />
        </a>
      </div>

      @if (!registrationComplete()) {
        <h2
          id="register-heading"
          class="font-montserrat mt-4 text-lg font-bold text-black lg:text-2xl"
        >
          Register for MyPortal
        </h2>
        <p class="font-lato mt-2 mb-4 text-base">
          Register your account to view and pay your bill, customize your
          account preferences, manage your services, and more.
        </p>

        <form
          [formGroup]="registerFormGroup"
          novalidate
          aria-labelledby="register-heading"
          (ngSubmit)="register()"
        >
          <label for="identifierId" class="comcast-input-label"
            >Business Account Number&nbsp;<span
              class="comcast-required-marker"
              aria-hidden="true"
              >*</span
            ></label
          >
          <input
            formControlName="identifier"
            id="identifierId"
            type="text"
            inputmode="numeric"
            pattern="[0-9]*"
            required
            name="identifier"
            autocomplete="off"
            (keydown)="onIdentifierKeydown($event)"
            (paste)="onIdentifierPaste($event)"
            aria-describedby="identifier-error register-help"
            [attr.aria-invalid]="
              submitted() && registerFormGroup.controls.identifier.invalid
            "
            [ngClass]="{
              'comcast-input-invalid':
                submitted() && registerFormGroup.controls.identifier.invalid,
            }"
            class="comcast-input"
            placeholder="Enter your Business Account Number"
          />
          <div aria-live="polite">
            @if (submitted() && registerFormGroup.controls.identifier.errors) {
              <span id="identifier-error" class="comcast-validation-error">
                @if (
                  registerFormGroup.controls.identifier.errors?.['required']
                ) {
                  Please enter your Business Account Number.
                } @else {
                  This doesn't look like a valid Business Account Number.
                }
              </span>
            }
          </div>
          <p id="register-help" class="font-lato text-brand-muted mt-1 text-sm">
            Your Account Number can be found on your bill. If you can't locate
            your business account number, call us at
            <a
              href="tel:8448864835,2"
              aria-label="Call us at 844-886-4835 option 2"
              class="font-lato text-sm font-semibold"
              >844-886-4835 option 2</a
            >
            for help.
          </p>

          <h3 class="font-montserrat mt-8 text-black">
            Enter your information
          </h3>
          <p class="font-lato mt-1">
            You will be assigned the role of an Admin of this account. As the
            Admin, you will have complete access to manage your account's
            features, services, users and billing.
          </p>

          <div class="my-4 flex flex-col gap-4 sm:flex-row sm:gap-3">
            <div class="min-w-0 flex-1">
              <label for="firstNameId" class="comcast-input-label"
                >First Name&nbsp;<span
                  class="comcast-required-marker"
                  aria-hidden="true"
                  >*</span
                ></label
              >
              <input
                formControlName="firstName"
                id="firstNameId"
                type="text"
                required
                name="firstName"
                autocomplete="given-name"
                aria-describedby="firstName-error"
                [attr.aria-invalid]="
                  submitted() && registerFormGroup.controls.firstName.invalid
                "
                [ngClass]="{
                  'comcast-input-invalid':
                    submitted() && registerFormGroup.controls.firstName.invalid,
                }"
                class="comcast-input"
                placeholder="Enter first name"
              />
              <div aria-live="polite">
                @if (
                  submitted() && registerFormGroup.controls.firstName.errors
                ) {
                  <span id="firstName-error" class="comcast-validation-error">
                    Please enter your first name.
                  </span>
                }
              </div>
            </div>

            <div class="min-w-0 flex-1">
              <label for="lastNameId" class="comcast-input-label"
                >Last Name&nbsp;<span
                  class="comcast-required-marker"
                  aria-hidden="true"
                  >*</span
                ></label
              >
              <input
                formControlName="lastName"
                id="lastNameId"
                type="text"
                required
                name="lastName"
                autocomplete="family-name"
                aria-describedby="lastName-error"
                [attr.aria-invalid]="
                  submitted() && registerFormGroup.controls.lastName.invalid
                "
                [ngClass]="{
                  'comcast-input-invalid':
                    submitted() && registerFormGroup.controls.lastName.invalid,
                }"
                class="comcast-input"
                placeholder="Enter last name"
              />
              <div aria-live="polite">
                @if (
                  submitted() && registerFormGroup.controls.lastName.errors
                ) {
                  <span id="lastName-error" class="comcast-validation-error">
                    Please enter your last name.
                  </span>
                }
              </div>
            </div>
          </div>

          <label for="emailId" class="comcast-input-label"
            >Email Address&nbsp;<span
              class="comcast-required-marker"
              aria-hidden="true"
              >*</span
            ></label
          >
          <input
            formControlName="email"
            id="emailId"
            type="email"
            required
            name="email"
            autocomplete="email"
            aria-describedby="email-error email-help"
            [attr.aria-invalid]="
              submitted() && registerFormGroup.controls.email.invalid
            "
            [ngClass]="{
              'comcast-input-invalid':
                submitted() && registerFormGroup.controls.email.invalid,
            }"
            class="comcast-input"
            placeholder="name@company.com"
          />
          <div aria-live="polite">
            @if (submitted() && registerFormGroup.controls.email.errors) {
              <span id="email-error" class="comcast-validation-error">
                @if (registerFormGroup.controls.email.errors?.['required']) {
                  Please enter your email address.
                } @else {
                  This doesn't look like a valid email address.
                }
              </span>
            }
          </div>
          <p id="email-help" class="font-lato text-brand-muted mt-1 text-sm">
            Enter the email address as provided when your services were
            purchased.
          </p>

          <div class="mt-4 flex flex-col gap-4 sm:flex-row sm:gap-3">
            <div class="min-w-0 flex-1">
              <label for="phoneId" class="comcast-input-label"
                >Phone Number</label
              >
              <input
                formControlName="phone"
                id="phoneId"
                type="tel"
                inputmode="tel"
                name="phone"
                autocomplete="tel"
                (input)="onPhoneInput($event)"
                aria-describedby="phone-error"
                [attr.aria-invalid]="
                  submitted() && registerFormGroup.controls.phone.invalid
                "
                [ngClass]="{
                  'comcast-input-invalid':
                    submitted() && registerFormGroup.controls.phone.invalid,
                }"
                class="comcast-input"
                placeholder="(555) 123-4567"
              />
              <div aria-live="polite">
                @if (submitted() && registerFormGroup.controls.phone.errors) {
                  <span id="phone-error" class="comcast-validation-error">
                    This doesn't look like a valid phone number.
                  </span>
                }
              </div>
            </div>

            <div class="min-w-0 flex-1">
              <label for="jobTitleId" class="comcast-input-label"
                >Job Title</label
              >
              <input
                formControlName="jobTitle"
                id="jobTitleId"
                type="text"
                name="jobTitle"
                autocomplete="organization-title"
                class="comcast-input"
                placeholder="Enter job title"
              />
            </div>
          </div>

          <label
            for="termsAcceptedId"
            class="mt-6 flex cursor-pointer items-start gap-2 text-sm select-none"
          >
            <input
              formControlName="termsAccepted"
              id="termsAcceptedId"
              type="checkbox"
              required
              aria-describedby="terms-error"
              [attr.aria-invalid]="
                submitted() && registerFormGroup.controls.termsAccepted.invalid
              "
              class="mt-0.5 h-4 w-4 shrink-0 cursor-pointer rounded accent-blue-600"
            />
            <span>
              Yes, I have read and accept the
              <a
                href="https://business.comcast.com/enterprise/terms-conditions/nitel"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Terms of Service (opens in new tab)"
                class="font-lato font-semibold hover:underline"
                >Terms of Service</a
              >.
            </span>
          </label>
          <div aria-live="polite">
            @if (
              submitted() && registerFormGroup.controls.termsAccepted.invalid
            ) {
              <span id="terms-error" class="comcast-validation-error">
                Please accept the Terms of Service to continue.
              </span>
            }
          </div>

          @if (errorMessage()) {
            <div class="comcast-validation-error" role="alert">
              {{ errorMessage() }}
            </div>
          }

          <div class="mt-8 flex">
            <button
              type="submit"
              [disabled]="isLoading()"
              [attr.aria-busy]="isLoading()"
              class="btn-primary"
            >
              @if (isLoading()) {
                <span class="sr-only">Loading, please wait</span>
                <div class="flex min-w-16 justify-center" aria-hidden="true">
                  <mat-spinner [diameter]="20"></mat-spinner>
                </div>
              } @else {
                REGISTER
              }
            </button>
          </div>

          <div class="font-lato mt-6 flex text-sm">
            Already have an account?&nbsp;
            <a
              routerLink="/login"
              class="font-lato text-sm font-bold hover:underline"
              >Sign in</a
            >
          </div>
        </form>
      } @else {
        <div
          class="font-lato mt-4 flex flex-col items-center text-base"
          role="status"
          aria-live="polite"
        >
          <h2 class="font-montserrat text-lg font-bold text-black lg:text-2xl">
            Thank you for registering!
          </h2>
          <p class="mt-4 text-center">
            Thanks for submitting your registration request. If the information
            you provided matches our records, you'll receive an email at
            <strong>{{ registerFormGroup.controls.email.value }}</strong>
            shortly with next steps to access your account.
          </p>
        </div>
        <div class="mt-6 flex justify-center">
          <a routerLink="/login" aria-label="Sign in" class="btn-primary">
            Sign In
          </a>
        </div>
      }
    </div>
  </section>
</div>
